import { TFunction } from "i18next";
import debounce from "lodash/debounce";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { ChangeEvent, Fragment, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { TableHeader } from "../../../components/table/TableHeader";
import { PageMeta } from "../../../model/meta/PageMeta";
import { PageDto } from "../../../model/shared/meta/PageDto";
import { GetTasksFilterDto } from "../../../model/task/dto/GetTasksFilterDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { taskSliceSelectors } from "../../../redux/task/task.selector";
import { taskActions } from "../../../redux/task/task.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import styled from "styled-components";
import { Breakpoints } from "../../../theme/Breakpoints";

import { TaskFilterReminder } from "../../../model/task/types/TaskFilterReminder.enum";
import { TaskKind } from "../../../model/task/shared/TasksKind.enum";
import { Spacing } from "../../../theme/Spacing";
import { Flex } from "../../../components/Flex";
import { useTheme } from "../../../ThemeContext";
import { Range } from "../../../model/shared/dto/GenericWithRange";
import { AtiraToast } from "../../../utils/AtiraToast";
import { rangeOptions } from "../../../utils/Date";
import { AtiraDateRange } from "../../../components/AtiraDateRange";
import { TestIDs } from "../../../tests/types/testIds";

const ColorDots = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const Dot = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const StyledSelect = styled(AtiraSelect)`
  flex: 1;
  min-width: 7.5rem;

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    min-width: 10rem;
  }

  .ant-select .ant-select-selector {
    border: 2px solid ${(props) => props.theme.transparent} !important;
  }
`;

const getDoneFilterOptions = (t: TFunction) => [
  {
    label: t("common.allStatuses"),
    value: null,
  },
  {
    label: t("common.done"),
    value: "true",
  },
  {
    label: t("common.notDone"),
    value: "false",
  },
];

const reminderOptions = (t: TFunction) => {
  return Object.entries({ ALL: null, ...TaskFilterReminder }).map(
    ([key, value]) => ({
      label: t(`common.${key.toLowerCase()}`),
      value,
    }),
  );
};

// v = { label: string, value: ThemeKeys }
const kindOptions = (t: TFunction) => {
  return Object.entries({ ALL: null, ...TaskKind }).map(([key, value]) => ({
    label: t(
      `common.${key.toLowerCase()}${key === TaskKind.ORPHAN ? "_task" : ""}`,
    ),
    value,
    "data-testid": TestIDs.tasks.subHeader.kindFilter.option(key),
  }));
};

type TaskTableHeaderProps = {
  showFilters: boolean;
};

export const TaskTableHeader: React.FC<TaskTableHeaderProps> = ({
  showFilters = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { getValues, setValue, control } = useFormContext<GetTasksFilterDto>();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);
  const tasksLoading = useAppSelector(taskSliceSelectors.selectAllTasksLoading);

  const dispatch = useAppDispatch();

  const doneOptions = getDoneFilterOptions(t);

  const onFetchTasks = useCallback(
    async (meta?: PageDto) => {
      try {
        await dispatch(
          taskActions.getAllTasks({
            userId,
            meta: meta || {
              count: tasksPageSize,
              page: tasksPage - 1,
            },
            ...omitBy(getValues(), isNil),
          }),
        ).unwrap();
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
      }
    },
    [dispatch, getValues, tasksPage, tasksPageSize, userId],
  );

  const renderThemeOption = (v: any) => {
    return (
      <Flex alignItems="center" gap="xs" justifyContent="space-between">
        <span>{v.label}</span>
        <ColorDots>
          <Dot
            color={theme[`${v.value?.toLowerCase()}Task` as keyof typeof theme]}
          />
        </ColorDots>
      </Flex>
    );
  };

  const onSearch = (
    { target }: ChangeEvent<HTMLInputElement>,
    meta: PageDto,
  ) => {
    dispatch(taskActions.setTasksTablePage(1));
    setValue("keyword", target.value);
    debouncedSearch();
  };

  const onFilterByStatus = (
    value?: "true" | "false" | null,
    meta?: PageDto,
  ) => {
    if (value === null) {
      setValue("done", value);
    } else if (value) {
      setValue("done", Boolean(value === "true"));
    }
    onFetchTasks(meta);
  };

  const onFilterByReminder = (value: TaskFilterReminder, meta: PageDto) => {
    setValue("reminder", value);
    onFetchTasks(meta);
  };

  const onFilterByRange = (value: Range, meta: PageDto) => {
    setValue("range", value);
    setValue("scope", undefined);
    onFetchTasks(meta);
  };

  const onFilterByScope = (value: [string, string], meta: PageDto) => {
    setValue("scope", value);
    setValue("range", undefined);
    onFetchTasks(meta);
  };

  // eslint doesn't like the debounce function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(() => onFetchTasks(PageMeta.create()), 1000),
    [onFetchTasks],
  );

  return (
    <Fragment>
      <TableHeader
        showFilters={showFilters}
        // Do not remove the arrow function here, it is necessary;
        onFetch={() => onFetchTasks()}
        onSearch={(e) => onSearch(e, PageMeta.create())}
        searchTestId={TestIDs.tasks.subHeader.search.input}
        onFetchLoading={tasksLoading}
        filters={[
          <Controller
            control={control}
            name="done"
            render={({ field: { value, onChange } }) => (
              <AtiraSelect
                value={doneOptions.find((option) => option.value === value)}
                onChange={(v) => {
                  onChange(v);
                  dispatch(taskActions.setTasksTablePage(1));
                  onFilterByStatus(
                    v as "true" | "false" | null,
                    PageMeta.create(),
                  );
                }}
                disabled={tasksLoading}
                loading={tasksLoading}
                options={doneOptions}
                containerStyle={{ width: "10rem" }}
              />
            )}
          />,

          <Controller
            control={control}
            name="scope"
            render={({ field: { onChange, value } }) => (
              <AtiraDateRange
                value={value}
                onChange={(dateStrings) => {
                  onChange(dateStrings);
                  dispatch(taskActions.setTasksTablePage(1));
                  onFilterByScope(
                    dateStrings as [string, string],
                    PageMeta.create(),
                  );
                }}
                loading={tasksLoading}
              />
            )}
          />,

          <Controller
            control={control}
            name="range"
            render={({ field: { onChange, value } }) => (
              <StyledSelect
                options={rangeOptions(t)}
                value={{
                  label: !value
                    ? t("entries.table.entry.filters.time.all")
                    : t(
                        `entries.table.entry.filters.time.${value.toLowerCase()}`,
                      ),
                  value,
                }}
                disabled={tasksLoading}
                loading={tasksLoading}
                onChange={(e) => {
                  onChange(e);
                  dispatch(taskActions.setTasksTablePage(1));
                  onFilterByRange(e as Range, PageMeta.create());
                }}
              />
            )}
          />,

          <Controller
            control={control}
            name="reminder"
            render={({ field: { onChange, value } }) => (
              <StyledSelect
                options={reminderOptions(t)}
                value={{
                  label: !value
                    ? t("common.allReminders")
                    : t(`common.${value.toLowerCase()}`),
                  value,
                }}
                disabled={tasksLoading}
                loading={tasksLoading}
                onChange={(e) => {
                  onChange(e);
                  dispatch(taskActions.setTasksTablePage(1));
                  onFilterByReminder(
                    e as TaskFilterReminder,
                    PageMeta.create(),
                  );
                }}
              />
            )}
          />,

          <Controller
            control={control}
            name="kind"
            render={({ field: { onChange, value } }) => (
              <StyledSelect
                options={kindOptions(t)}
                value={{
                  label: !value
                    ? t("common.all")
                    : t(
                        `common.${value.toLowerCase()}${value === TaskKind.ORPHAN ? "_task" : ""}`,
                      ),
                  value,
                }}
                disabled={tasksLoading}
                loading={tasksLoading}
                onChange={(e) => {
                  onChange(e);
                  dispatch(taskActions.setTasksTablePage(1));
                  onFetchTasks(PageMeta.create());
                }}
                labelRender={renderThemeOption}
                optionRender={renderThemeOption}
                className={TestIDs.tasks.subHeader.kindFilter.open}
              />
            )}
          />,
        ]}
      />
    </Fragment>
  );
};
